import './SaturationCurveChart.scss';
import { ReactElement, ReactNode, useMemo, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  Scatter,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { CARTESIAN_GRID_STROKE_WIDTH, TICK_STYLE } from '@Components/Charts/ChartConstants';
import { buildEquidistantScale } from '@Components/Charts/ChartUtils';
import { Placement } from '../../../Components/designConstants';
import { Dropdown } from '../../../Components/Dropdown';
import { ToggleSwitch } from '../../../Components/ToggleSwitch';
import { Neutral500, Neutral700 } from '../../../utils/colors';
import { formatAxisSpend, formatSpend } from '../forecasting-constants';

// One observed week. `days` = days of data in it (1-7); missing means a full week.
interface ScatterPoint {
  spend: number;
  volume: number;
  days?: number;
}

// Lean series shape the chart needs — works with both mock and real (fixture) data.
export interface ChartSeries {
  name: string;
  color: string;
  scatter: ScatterPoint[];
  curve: {
    spend: number;
    fit: number;
    lwrPred: number;
    uprPred: number;
    extrapolated: string;
  }[];
}

// A selectable y-axis metric (Value / CPA / ROAS). Each view carries its own pre-transformed
// series plus the formatters + label for that metric — see scaling-chart-utils.buildYAxisViews.
export interface YAxisView {
  key: string;
  // Metric/axis label — the units the y-axis reads in (Volume, Revenue, CPA, ROAS, Marginal ROAS).
  label: string;
  // Named plot for the picker (Outcome / Efficiency / Marginal) — Budget Builder parity, so users
  // pick a plot rather than "think in axes." Multiple views can share one (CPA and ROAS are both
  // Efficiency), but never within a single outcome's view set, so it stays a unique picker key.
  plotType: string;
  series: ChartSeries[];
  formatY: (value: number) => string;
  formatAxisY: (value: number) => string;
  // Cap the shaded prediction band to a sane envelope. Used for the CPA/ROAS efficiency views,
  // whose transformed bounds can balloon; the Volume/Revenue views show the full band.
  truncateBand?: boolean;
}

const X_TICK_COUNT = 5;
const Y_TICK_COUNT = 4;
const POINT_RADIUS = 3;
const LINE_WIDTH = 2;
const FULL_WEEK_DAYS = 7;
const PARTIAL_WEEK_STROKE = 1.5;

interface ScatterShapeProps {
  cx?: number;
  cy?: number;
  fill?: string;
  payload?: ScatterPoint;
}

// A week with fewer than 7 days of data (e.g. the partial weeks at the window's
// edges) draws hollow in the same color, with how many days it covers on hover.
const ScatterShape = ({ cx, cy, fill, payload }: ScatterShapeProps): ReactElement | null => {
  if (cx === undefined || cy === undefined) return null;
  const days = payload?.days ?? FULL_WEEK_DAYS;
  if (days >= FULL_WEEK_DAYS) {
    return <circle cx={cx} cy={cy} r={POINT_RADIUS} fill={fill} fillOpacity={0.85} />;
  }
  return (
    <circle
      className='saturation-scatter-partial'
      cx={cx}
      cy={cy}
      r={POINT_RADIUS}
      fill='white'
      stroke={fill}
      strokeWidth={PARTIAL_WEEK_STROKE}
    >
      <title>{`${days} of ${FULL_WEEK_DAYS} days of data`}</title>
    </circle>
  );
};
const BAND_OPACITY = 0.18;
const SCALE_HEADROOM = 1.05;
// Prediction bounds can balloon far above the fitted line (worst near low spend, and hugely so
// for transformed CPA/ROAS). Cap the shaded band at this multiple of the fitted value per point
// so the band stays readable instead of swamping the plot; the fitted line is never truncated.
const BAND_MAX_FIT_MULTIPLE = 3;
// Dotted (not dashed) to match the ggplot `extrapolated` linetype.
const EXTRAPOLATED_DASH = '1 5';

// One point's shaded prediction band. When truncate is set (CPA/ROAS views) it's clamped to a
// sane envelope around the fitted value — returning null (a gap) only if the fit isn't finite,
// and collapsing a non-finite upper bound (e.g. a CPA bound at a zero prediction) to the cap so
// the band stays continuous. Otherwise (Volume/Revenue) the full prediction band is shown as-is.
const bandFor = (
  p: { fit: number; lwrPred: number; uprPred: number },
  truncate: boolean
): [number, number] | null => {
  if (!truncate) {
    return Number.isFinite(p.lwrPred) && Number.isFinite(p.uprPred) ? [p.lwrPred, p.uprPred] : null;
  }
  if (!Number.isFinite(p.fit)) return null;
  const cap = Math.abs(p.fit) * BAND_MAX_FIT_MULTIPLE;
  const upper = Math.min(Number.isFinite(p.uprPred) ? p.uprPred : cap, cap);
  const lower = Math.min(Number.isFinite(p.lwrPred) ? Math.max(p.lwrPred, 0) : 0, upper);
  return [lower, upper];
};

interface SeriesGraphData {
  name: string;
  color: string;
  scatter: ScatterPoint[];
  band: { spend: number; band: [number, number] | null }[];
  historical: { spend: number; fit: number }[];
  extrapolated: { spend: number; fit: number }[];
}

// Split one series' fitted curve into the data arrays each Recharts series needs:
// a range band, a solid historical line, and a dotted extrapolated line. The
// extrapolated line re-includes the last historical point so the two lines join.
// Non-finite band bounds (e.g. a transformed CPA bound at a zero prediction) become null gaps.
const toGraphData = (series: ChartSeries, truncateBand: boolean): SeriesGraphData => {
  const band = series.curve.map(p => ({
    spend: p.spend,
    band: bandFor(p, truncateBand)
  }));
  const historical = series.curve
    .filter(p => p.extrapolated === 'historical')
    .map(p => ({ spend: p.spend, fit: p.fit }));
  const extrapolatedPoints = series.curve
    .filter(p => p.extrapolated === 'extrapolated')
    .map(p => ({ spend: p.spend, fit: p.fit }));
  const lastHistorical = historical[historical.length - 1];
  const extrapolated =
    extrapolatedPoints.length > 0 && lastHistorical
      ? [lastHistorical, ...extrapolatedPoints]
      : extrapolatedPoints;

  return {
    name: series.name,
    color: series.color,
    scatter: series.scatter,
    band,
    historical,
    extrapolated
  };
};

const nearestPoint = (curve: ChartSeries['curve'], spend: number) =>
  curve.reduce((best, p) => (Math.abs(p.spend - spend) < Math.abs(best.spend - spend) ? p : best));

interface CurveTooltipProps {
  // active / label are injected by Recharts when it clones the content element.
  active?: boolean;
  label?: number | string;
  series: ChartSeries[];
  formatY: (value: number) => string;
}

// Hover tooltip: shows the fitted value and the upper/lower prediction bounds at the
// nearest spend increment on the curve.
const CurveTooltip = ({
  active,
  label,
  series,
  formatY
}: CurveTooltipProps): ReactElement | null => {
  if (!active || label === undefined || label === null) return null;
  const spend = Number(label);
  const fmt = (value: number): string => (Number.isFinite(value) ? formatY(value) : '--');

  return (
    <div className='saturation-curve-tooltip'>
      <div className='tooltip-spend'>{formatSpend(spend)}</div>
      {series.map(s => {
        const point = nearestPoint(s.curve, spend);
        return (
          <div className='tooltip-rows' key={s.name}>
            <div className='tooltip-row'>
              <span className='tooltip-key'>Upper</span>
              <span className='tooltip-val'>{fmt(point.uprPred)}</span>
            </div>
            <div className='tooltip-row tooltip-row-fit'>
              <span className='tooltip-key'>Fitted</span>
              <span className='tooltip-val'>{fmt(point.fit)}</span>
            </div>
            <div className='tooltip-row'>
              <span className='tooltip-key'>Lower</span>
              <span className='tooltip-val'>{fmt(point.lwrPred)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface SaturationCurveChartProps {
  // Selectable y-axis metrics; the first is the default view.
  views: YAxisView[];
  title?: ReactNode;
  xLabel?: string;
}

export const SaturationCurveChart = ({
  views,
  title = 'Saturation Curve',
  xLabel = 'Weekly Spend'
}: SaturationCurveChartProps): ReactElement => {
  const [showScatter, setShowScatter] = useState(true);
  const [viewKey, setViewKey] = useState(views[0]?.key);

  const selected = views.find(v => v.key === viewKey) ?? views[0];
  const { series: data, formatY, formatAxisY } = selected;
  const truncateBand = selected.truncateBand ?? false;

  const graphData = useMemo(
    () => data.map(s => toGraphData(s, truncateBand)),
    [data, truncateBand]
  );

  const { spendScale, volumeScale } = useMemo(() => {
    let maxSpend = 0;
    let maxVolume = 0;
    const consider = (value: number) => {
      if (Number.isFinite(value)) {
        maxVolume = Math.max(maxVolume, value);
      }
    };
    data.forEach(series => {
      series.scatter.forEach(p => {
        maxSpend = Math.max(maxSpend, p.spend);
        consider(p.volume);
      });
      series.curve.forEach(p => {
        maxSpend = Math.max(maxSpend, p.spend);
        consider(p.fit);
        // Scale to the (possibly truncated) band so a ballooning efficiency interval can't blow
        // out the y-axis; Volume/Revenue views use the full band and scale to it as before.
        consider(bandFor(p, truncateBand)?.[1] ?? p.fit);
      });
    });
    return {
      spendScale: buildEquidistantScale(0, maxSpend, X_TICK_COUNT),
      volumeScale: buildEquidistantScale(0, maxVolume * SCALE_HEADROOM, Y_TICK_COUNT)
    };
  }, [data, truncateBand]);

  return (
    <div className='saturation-curve-chart'>
      <div className='saturation-curve-header'>
        <div className='saturation-curve-title'>{title}</div>
        <div className='saturation-curve-controls'>
          {views.length > 1 && (
            <div className='saturation-curve-control'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                value={selected.plotType}
                options={views.map(v => v.plotType)}
                onChange={plotType =>
                  setViewKey(views.find(v => v.plotType === plotType)?.key ?? views[0].key)
                }
              />
            </div>
          )}
          <div className='saturation-curve-control'>
            <ToggleSwitch
              className='saturation-curve-toggle'
              label='Raw weekly data'
              checked={showScatter}
              onChange={setShowScatter}
            />
          </div>
        </div>
      </div>
      <div className='saturation-curve-body'>
        <div className='saturation-curve-graph'>
          <AutoSizer
            renderProp={({ width, height }) => (
              <ComposedChart
                width={width ?? 0}
                height={height ?? 0}
                margin={{ top: 16, right: 16, bottom: 24, left: 28 }}
              >
                <CartesianGrid
                  stroke={Neutral500}
                  strokeOpacity={0.25}
                  strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                />
                <XAxis
                  type='number'
                  dataKey='spend'
                  domain={spendScale.domain}
                  ticks={spendScale.ticks}
                  tickFormatter={formatAxisSpend}
                  tick={TICK_STYLE}
                  tickLine={false}
                  allowDuplicatedCategory={false}
                  label={{ value: xLabel, position: 'insideBottom', offset: -16, fill: Neutral700 }}
                />
                <YAxis
                  type='number'
                  width={72}
                  domain={volumeScale.domain}
                  ticks={volumeScale.ticks}
                  tickFormatter={formatAxisY}
                  tick={TICK_STYLE}
                  tickLine={false}
                  axisLine={false}
                  label={{
                    value: selected.label,
                    angle: -90,
                    position: 'insideLeft',
                    offset: 0,
                    style: { textAnchor: 'middle', fill: Neutral700 }
                  }}
                />
                {/* ROAS break-even reference at 1x: for Marginal ROAS the next dollar returns
                    exactly a dollar; for average ROAS it's the total-spend profitability floor. */}
                {(selected.key === 'marginalRoas' || selected.key === 'roas') && (
                  <ReferenceLine
                    y={1}
                    stroke={Neutral500}
                    strokeDasharray='4 4'
                    ifOverflow='extendDomain'
                    label={{
                      value: 'Break-even (1x)',
                      position: 'insideBottomRight',
                      fill: Neutral700,
                      fontSize: 11
                    }}
                  />
                )}
                {/* Prediction bands first so they sit behind everything else. */}
                {graphData.map(c => (
                  <Area
                    key={`${c.name}-band`}
                    data={c.band}
                    dataKey='band'
                    type='monotone'
                    stroke='transparent'
                    fill={c.color}
                    fillOpacity={BAND_OPACITY}
                    activeDot={false}
                    isAnimationActive={false}
                    connectNulls
                    legendType='none'
                  />
                ))}
                {/* Raw weekly observations (toggleable). */}
                {showScatter &&
                  graphData.map(c => (
                    <Scatter
                      key={`${c.name}-scatter`}
                      data={c.scatter}
                      dataKey='volume'
                      fill={c.color}
                      isAnimationActive={false}
                      shape={<ScatterShape />}
                      legendType='none'
                    />
                  ))}
                {/* Fitted curves: solid historical, dotted extrapolated. */}
                {graphData.map(c => (
                  <Line
                    key={`${c.name}-historical`}
                    data={c.historical}
                    dataKey='fit'
                    type='monotone'
                    stroke={c.color}
                    strokeWidth={LINE_WIDTH}
                    dot={false}
                    isAnimationActive={false}
                    legendType='none'
                  />
                ))}
                {graphData.map(c => (
                  <Line
                    key={`${c.name}-extrapolated`}
                    data={c.extrapolated}
                    dataKey='fit'
                    type='monotone'
                    stroke={c.color}
                    strokeWidth={LINE_WIDTH}
                    strokeDasharray={EXTRAPOLATED_DASH}
                    dot={false}
                    isAnimationActive={false}
                    legendType='none'
                  />
                ))}
                <Tooltip
                  content={<CurveTooltip series={data} formatY={formatY} />}
                  cursor={{ stroke: Neutral500, strokeWidth: 1 }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            )}
          />
        </div>
        <SaturationCurveLegend />
      </div>
    </div>
  );
};

const SaturationCurveLegend = (): ReactElement => (
  <div className='saturation-curve-legend'>
    <div className='legend-section'>
      <div className='legend-section-title'>Data Source</div>
      <div className='legend-row'>
        <svg className='legend-line-swatch' viewBox='0 0 24 8'>
          <line x1='0' y1='4' x2='24' y2='4' stroke={Neutral700} strokeWidth='2' />
        </svg>
        <span>historical</span>
      </div>
      <div className='legend-row'>
        <svg className='legend-line-swatch' viewBox='0 0 24 8'>
          <line
            x1='0'
            y1='4'
            x2='24'
            y2='4'
            stroke={Neutral700}
            strokeWidth='2'
            strokeDasharray={EXTRAPOLATED_DASH}
          />
        </svg>
        <span>extrapolated</span>
      </div>
    </div>
  </div>
);
